import React, { useCallback, useMemo, useRef } from "react";
import {
    CounterpartyReviewField,
    GenericCounterpartyReviewField,
} from "@common/types/counterpartyFieldReview";
import { CounterpartyFieldReviewDto } from "@common/schemas/counterpartyFieldReview.schema";
import { WorkspaceMember } from "@common/types/workspace";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { useUser } from "@hooks/useUser";
import { useScope } from "@state/scopeContext.lib";
import { DetailsAccordionItem } from "@design-system/Accordion/DetailsAccordionItem";
import {
    addCounterpartyFieldReviews,
    revokeCounterpartyFieldReview,
    updateCounterpartyFieldReviewsInCache,
} from "@api/counterparty.api";
import {
    CounterpartyDetailsReview,
    CounterpartyFieldReviewState,
    CounterpartyFieldReviewer,
} from "./CounterpartyDetailsReview";
import { CounterpartyTinReviewRow } from "./CounterpartyTinReviewRow";
import { toFieldReviewer } from "./toFieldReviewer";
import { CounterpartyDirectoryViewRow } from "./counterpartyDirectoryViewTypes";

function buildReviewers(
    reviews: CounterpartyFieldReviewDto[],
    members: WorkspaceMember[],
    field: CounterpartyReviewField,
): CounterpartyFieldReviewer[] {
    return reviews
        .filter((r) => r.field === field)
        .map((r) => toFieldReviewer(r, members));
}

const EMPTY_REVIEW_STATE: CounterpartyFieldReviewState = {
    [CounterpartyReviewField.BASIC_INFO]: [],
    [CounterpartyReviewField.ADDRESS]: [],
    [CounterpartyReviewField.ELIGIBLE_1099]: [],
    [CounterpartyReviewField.TAX_CLASSIFICATION]: [],
};

interface Props {
    row: CounterpartyDirectoryViewRow;
}

export const CounterpartyReviewAccordionItem: React.FC<Props> = ({ row }) => {
    const cp = row.counterparty;
    const { workspaceId } = useScope();
    const { data: workspaceMembers = [] } = useCurrentWorkspaceMembers();
    const currentUser = useUser();
    // Guard against double-submits per field, so an in-flight request on one
    // field doesn't silently drop a mark/undo click on a different field.
    const inFlightFieldsRef = useRef<Set<GenericCounterpartyReviewField>>(
        new Set(),
    );

    const reviewState = useMemo((): CounterpartyFieldReviewState => {
        const reviews = row.fieldReviews;
        if (!reviews) {
            return EMPTY_REVIEW_STATE;
        }
        return {
            [CounterpartyReviewField.BASIC_INFO]: buildReviewers(
                reviews,
                workspaceMembers,
                CounterpartyReviewField.BASIC_INFO,
            ),
            [CounterpartyReviewField.ADDRESS]: buildReviewers(
                reviews,
                workspaceMembers,
                CounterpartyReviewField.ADDRESS,
            ),
            [CounterpartyReviewField.ELIGIBLE_1099]: buildReviewers(
                reviews,
                workspaceMembers,
                CounterpartyReviewField.ELIGIBLE_1099,
            ),
            [CounterpartyReviewField.TAX_CLASSIFICATION]: buildReviewers(
                reviews,
                workspaceMembers,
                CounterpartyReviewField.TAX_CLASSIFICATION,
            ),
        };
    }, [row.fieldReviews, workspaceMembers]);

    const handleMarkReviewed = useCallback(
        async (field: GenericCounterpartyReviewField) => {
            if (inFlightFieldsRef.current.has(field) || !cp || !workspaceId) {
                return;
            }
            inFlightFieldsRef.current.add(field);
            try {
                const reviews = await addCounterpartyFieldReviews(
                    workspaceId,
                    cp.id,
                    [field],
                );
                const isSuperseded = (r: CounterpartyFieldReviewDto) =>
                    reviews.some(
                        (nr) => nr.field === r.field && nr.userId === r.userId,
                    );
                updateCounterpartyFieldReviewsInCache(
                    workspaceId,
                    cp.id,
                    (existing) => [
                        ...existing.filter((r) => !isSuperseded(r)),
                        ...reviews,
                    ],
                );
            } catch {
                // error toast handled by axios interceptor
            } finally {
                inFlightFieldsRef.current.delete(field);
            }
        },
        [cp, workspaceId],
    );

    const handleUndoReview = useCallback(
        async (field: GenericCounterpartyReviewField) => {
            if (inFlightFieldsRef.current.has(field) || !cp || !workspaceId) {
                return;
            }
            inFlightFieldsRef.current.add(field);
            try {
                await revokeCounterpartyFieldReview(workspaceId, cp.id, field);
                updateCounterpartyFieldReviewsInCache(
                    workspaceId,
                    cp.id,
                    (existing) =>
                        existing.filter(
                            (r) =>
                                !(
                                    r.userId === currentUser.id &&
                                    r.field === field
                                ),
                        ),
                );
            } catch {
                // error toast handled by axios interceptor
            } finally {
                inFlightFieldsRef.current.delete(field);
            }
        },
        [cp, workspaceId, currentUser.id],
    );

    if (!cp) {
        return null;
    }

    return (
        <DetailsAccordionItem value="review" label="Review">
            <CounterpartyDetailsReview
                reviewState={reviewState}
                currentUserId={currentUser.id}
                onMarkReviewed={handleMarkReviewed}
                onUndoReview={handleUndoReview}
            >
                {cp.workspaceId && (
                    <CounterpartyTinReviewRow
                        ids={{
                            workspaceId: cp.workspaceId,
                            counterpartyId: cp.id,
                        }}
                        currentUserId={currentUser.id}
                        members={workspaceMembers}
                    />
                )}
            </CounterpartyDetailsReview>
        </DetailsAccordionItem>
    );
};
